import { api } from './api';
import { offlineStorage } from './offlineStorage';

let isSyncing = false;
let isPresetSyncing = false;
let isPatientCreateSyncing = false;
let fullSyncPromise: Promise<void> | null = null;
type PatientSummary = { id: string; patientCode: string; name: string };

type SyncListener = (pendingCount: number) => void;
const listeners: Set<SyncListener> = new Set();

export function onSyncStateChange(listener: SyncListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function notifyListeners(): Promise<void> {
  const count = await offlineStorage.countAllPending();
  listeners.forEach((listener) => listener(count));
}

function getApiStatus(error: unknown): number | null {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : null;
  }
  return null;
}

function isPermanentClientError(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429;
}

function normalizeName(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

export async function syncPendingExams(): Promise<{
  synced: number;
  failed: number;
  total: number;
}> {
  if (isSyncing) {
    return { synced: 0, failed: 0, total: 0 };
  }

  if (!navigator.onLine) {
    return { synced: 0, failed: 0, total: 0 };
  }

  isSyncing = true;

  try {
    const pendingExams = await offlineStorage.getPendingExamsForSync();

    if (pendingExams.length === 0) {
      return { synced: 0, failed: 0, total: 0 };
    }

    const pendingPatientCreates = await offlineStorage.getAllPendingPatientCreates();
    const unresolvedTempPatientIds = new Set(pendingPatientCreates.map((item) => item.tempId));

    let synced = 0;
    let failed = 0;

    for (const exam of pendingExams) {
      if (
        unresolvedTempPatientIds.has(exam.patientId) ||
        exam.patientId.startsWith('offline-patient-')
      ) {
        continue;
      }

      try {
        const { id, savedAt, failedAt, syncError, syncErrorStatus, ...requestBody } = exam;

        await api.post('/api/exams', requestBody);

        if (id !== undefined) {
          await offlineStorage.delete(id);
        }
        synced++;

        await notifyListeners();
      } catch (error: unknown) {
        const status = getApiStatus(error);

        if (status === 409) {
          if (exam.id !== undefined) {
            await offlineStorage.delete(exam.id);
          }
          synced++;
          await notifyListeners();
          continue;
        }

        if (status !== null && isPermanentClientError(status)) {
          if (exam.id !== undefined) {
            await offlineStorage.markExamSyncFailed(
              exam.id,
              status,
              error instanceof Error ? error.message : `HTTP ${status}`
            );
          }
          failed++;
          await notifyListeners();
          continue;
        }

        console.error('Exam sync error:', error);
        failed++;
      }
    }

    return { synced, failed, total: pendingExams.length };
  } finally {
    isSyncing = false;
    await notifyListeners();
  }
}

export async function syncPendingPresetSyncs(): Promise<{
  synced: number;
  failed: number;
  total: number;
}> {
  if (isPresetSyncing) {
    return { synced: 0, failed: 0, total: 0 };
  }

  if (!navigator.onLine) {
    return { synced: 0, failed: 0, total: 0 };
  }

  isPresetSyncing = true;

  try {
    const pendingItems = await offlineStorage.getAllPendingPresetSyncs();

    if (pendingItems.length === 0) {
      return { synced: 0, failed: 0, total: 0 };
    }

    let synced = 0;
    let failed = 0;

    for (const item of pendingItems) {
      try {
        if (item.action === 'create') {
          await api.post('/api/presets', item.payload);
        } else if (item.action === 'update' && item.presetId) {
          await api.put(`/api/presets/${item.presetId}`, item.payload);
        } else {
          failed++;
          continue;
        }

        if (item.id !== undefined) {
          await offlineStorage.deletePendingPresetSync(item.id);
        }
        synced++;
        await notifyListeners();
      } catch (error) {
        console.error('Preset sync error:', error);
        failed++;
      }
    }

    return { synced, failed, total: pendingItems.length };
  } finally {
    isPresetSyncing = false;
    await notifyListeners();
  }
}

export async function syncPendingPatientCreates(): Promise<{
  synced: number;
  failed: number;
  total: number;
}> {
  if (isPatientCreateSyncing) {
    return { synced: 0, failed: 0, total: 0 };
  }

  if (!navigator.onLine) {
    return { synced: 0, failed: 0, total: 0 };
  }

  isPatientCreateSyncing = true;

  try {
    const pendingItems = await offlineStorage.getAllPendingPatientCreates();
    if (pendingItems.length === 0) {
      return { synced: 0, failed: 0, total: 0 };
    }

    let synced = 0;
    let failed = 0;

    for (const item of pendingItems) {
      try {
        const createdPatient = await api.post<{ id: string }>('/api/patients', item.payload);
        await offlineStorage.replacePendingExamPatientId(item.tempId, createdPatient.id);
        if (item.id !== undefined) {
          await offlineStorage.deletePendingPatientCreate(item.id);
        }
        synced++;
        await notifyListeners();
      } catch (error: unknown) {
        const status = getApiStatus(error);

        if (status === 409) {
          try {
            const patients = await api.get<PatientSummary[]>('/api/patients');
            const expectedName = normalizeName(item.payload.name);
            const matched =
              patients.find(
                (patient) =>
                  patient.patientCode === item.payload.patientCode &&
                  normalizeName(patient.name) === expectedName
              ) ??
              patients.find(
                (patient) =>
                  patient.patientCode === item.payload.patientCode
              );
            if (matched) {
              await offlineStorage.replacePendingExamPatientId(item.tempId, matched.id);
              if (item.id !== undefined) {
                await offlineStorage.deletePendingPatientCreate(item.id);
              }
              synced++;
              await notifyListeners();
              continue;
            }
          } catch (resolveError) {
            console.error('Failed to resolve conflicted patient id:', resolveError);
          }
          failed++;
          continue;
        }

        console.error('Patient create sync error:', error);
        failed++;
      }
    }

    return { synced, failed, total: pendingItems.length };
  } finally {
    isPatientCreateSyncing = false;
    await notifyListeners();
  }
}

export function initSyncService(): () => void {
  const runSync = () => {
    if (fullSyncPromise) {
      return fullSyncPromise;
    }

    fullSyncPromise = (async () => {
      try {
        // 患者作成の同期を先に完了させてから、患者ID依存の検査同期を実行する。
        await syncPendingPatientCreates();
        await syncPendingExams();
        await syncPendingPresetSyncs();
      } catch (error) {
        console.error('Sync failed:', error);
      }
    })().finally(() => {
      fullSyncPromise = null;
    });

    return fullSyncPromise;
  };

  const timeoutId = setTimeout(() => {
    runSync();
  }, 3000);

  const handleOnline = () => {
    runSync();
  };

  window.addEventListener('online', handleOnline);

  return () => {
    clearTimeout(timeoutId);
    window.removeEventListener('online', handleOnline);
  };
}
